import React, { useEffect, useMemo, useState } from "react";
import "./systemDashboard.scss";
import { useImmer } from "use-immer";
import {
  ALARM_WEBSOCKET_PATH,
  APP_BASE_URL,
  APP_BASE_URL_ALARM,
  APP_BASE_URL_ALARM_SOCKET,
  SYSTEM_ALARMS,
} from "../../Common/EndPoints";
import toast from "react-hot-toast";
import axios from "axios";
import { Autocomplete, Button, Chip, TextField } from "@mui/material";
import { AutocompleteStationListPop } from "../AutocompleteStationListPop/AutocompleteStationListPop";
import { extractParsedDataFromPermissions, extractUserDataFromPermission, getMoreRecentDate } from "../../Utils/Utils";
import { getParsedLocalStorage } from "../../Utils/storage-utils";
import { selectedCustomersSelector } from "../../features/authentication/auth-slice";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import Loader from "../Loader/Loader";
import MoDataComp from "../no-data-comp/MoDataComp";
import { updateSelectedSidebarKey } from "../NewSidebar/sidebarSlice";
import { setTemporaryAlarmFilters } from "../../features/Alarms/alarmsSlice";
import { useNavigate } from "react-router-dom";
function formatToDDMMYYYY_HHMMSS(timestamp) {
  if (!timestamp || !moment(timestamp).isValid()) return null;
  return moment(timestamp).format("DD/MM/YYYY HH:mm:ss");
}
const StatusCard = ({ data }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { software_code, software_id, failure_cnt, healthy_cnt, prediction_cnt, total_cnt, last_alarm_created_dt } =
    data;

  const handlCradClick = () => {
    const params = {
      systems: [
        {
          systemId: software_id,
          systemName: software_code,
        },
      ],
      limit: 20,
      offset: 0,
      is_active: true,
    };
    dispatch(updateSelectedSidebarKey("ALARM"));
    dispatch(setTemporaryAlarmFilters(params));
    navigate("/alarms");
  };

  let formatted_time = formatToDDMMYYYY_HHMMSS(last_alarm_created_dt) ?? "No Alarm History";
  return (
    <div className="status-card" onClick={handlCradClick} style={{ cursor: "pointer" }}>
      <div className="status-card__header">{software_code}</div>
      <div className="status-card__content">
        <div className="status-row status-row__predictive">
          <span className="number">{prediction_cnt}</span> <span className="label predictive">Predictive</span>
        </div>
        <div className="status-row status-row__failure">
          <span className="number">{failure_cnt}</span> <span className="label failure">Failure</span>
        </div>
        <div className="status-row status-row__healthy">
          <span className="number">{healthy_cnt}</span> <span className="label healthy">Healthy</span>
        </div>
        <div className="status-row status-row__total">
          <span className="number">{total_cnt}</span> <span className="label total">Total</span>
        </div>
      </div>
      <div className="status-card__footer">
        <div className="status-card__time">{formatted_time}</div>
      </div>
    </div>
  );
};

const initSystemData = {
  isLoading: false,
  data: [],
};

function sortByLastAlarmRecentToOld(data) {
  return [...data].sort((a, b) => {
    const dateA = a?.last_alarm_created_dt;
    const dateB = b?.last_alarm_created_dt;
    const validA = dateA && moment(dateA).isValid();
    const validB = dateB && moment(dateB).isValid();

    if (validA && validB) {
      return moment(dateB).valueOf() - moment(dateA).valueOf();
    }
    if (!validA && validB) return 1;
    if (validA && !validB) return -1;
    return 0;
  });
}
const getSystemWiseGrouped = (data = []) => {
  let map = {};

  data.forEach((_data) => {
    const { software_code, software_id, software_alias, status, last_alarm_created_dt = null } = _data;

    const { total, critical, warning, maintenance } = status;

    const failure_cnt = critical;
    const prediction_cnt = warning;
    const healthy_cnt = total - critical - warning + maintenance;
    const total_cnt = total;
    if (software_code in map) {
      map[software_code].total_cnt = map[software_code].total_cnt + total_cnt;
      map[software_code].failure_cnt = map[software_code].failure_cnt + failure_cnt;
      map[software_code].healthy_cnt = map[software_code].healthy_cnt + healthy_cnt;
      map[software_code].prediction_cnt = map[software_code].prediction_cnt + prediction_cnt;
      map[software_code].last_alarm_created_dt = getMoreRecentDate(
        map[software_code].last_alarm_created_dt,
        last_alarm_created_dt,
      );
    } else {
      map[software_code] = {
        software_code,
        software_id,
        software_alias,
        failure_cnt,
        healthy_cnt,
        prediction_cnt,
        total_cnt,
        last_alarm_created_dt,
      };
    }
  });

  return map;
};

const getInitFilterFrom = () => {
  let zone_ids = [];
  let div_ids = [];
  let customer_ids = [];
  let software_ids = [];
  return {
    zone_ids,
    div_ids,
    customer_ids,
    software_ids,
  };
};

const getStrIdListFromOptions = (dd = []) => {
  return dd.map((obj) => obj.id).join(",");
};

const getApiFilterParams = (filterForm, total_filtered_customers) => {
  let customer_ids = filterForm.customer_ids.length
    ? getStrIdListFromOptions(filterForm.customer_ids)
    : getStrIdListFromOptions(total_filtered_customers);
  return { customer_ids };
};
const getStrCustomerIds = (list = []) => {
  return list.map((customer) => customer.customerId).join(",");
};

function SystemDashboard() {
  const user_permissions = getParsedLocalStorage("permissions");
  const parsedPermissions = useMemo(() => {
    return extractParsedDataFromPermissions(user_permissions);
  }, []);

  const selectedCustomersList = useSelector(selectedCustomersSelector);
  const permittedCustomerIds = useMemo(() => {
    const _list = selectedCustomersList;
    if (!_list) return [];
    return _list.map((customer) => customer.customerId);
  }, [selectedCustomersList]);
  const [systemAlarmsData, setSystemAlarmsData] = useImmer(initSystemData);
  const [filterForm, setFilterForm] = useImmer(() => getInitFilterFrom());
  const [finalFilters, setFinalFilters] = useState(() => getInitFilterFrom());

  const systemWiseGroupedAlarmsData = useMemo(() => {
    return getSystemWiseGrouped(systemAlarmsData?.data);
  }, [systemAlarmsData]);

  const zone_dd = useMemo(() => {
    return parsedPermissions.zones;
  }, []);

  const div_dd = useMemo(() => {
    let total_divs = parsedPermissions.divisions;
    if (filterForm.zone_ids.length) {
      const ids = filterForm.zone_ids.map((obj) => obj.id);
      total_divs = parsedPermissions.divisions.filter((div_obj) => ids.includes(div_obj.zone.id));
      const filtered_form_div_ids = filterForm.div_ids.filter((selected_div_obj) => {
        return total_divs.find((obj) => obj.id === selected_div_obj.id);
      });
      setFilterForm((prev) => {
        prev.div_ids = filtered_form_div_ids;
      });
    }

    return total_divs;
  }, [filterForm.zone_ids]);
  const customer_dd = useMemo(() => {
    let filtered_stations = parsedPermissions.stations.filter((obj) => permittedCustomerIds.includes(obj.id));
    if (filterForm.div_ids.length) {
      const selected_div_ids = filterForm.div_ids.map((obj) => obj.id);
      filtered_stations = filtered_stations.filter((stationObj) => selected_div_ids.includes(stationObj.division.id));
    } else if (filterForm.zone_ids.length) {
      const selected_zone_ids = filterForm.zone_ids.map((obj) => obj.id);
      filtered_stations = filtered_stations.filter((stationObj) => selected_zone_ids.includes(stationObj.zone.id));
    }
    const filtered_form_stn_ids = filterForm.customer_ids.filter((selected_stn_obj) => {
      return filtered_stations.find((obj) => obj.id === selected_stn_obj.id);
    });
    setFilterForm((prev) => {
      prev.customer_ids = filtered_form_stn_ids;
    });
    return filtered_stations;
  }, [permittedCustomerIds, filterForm.div_ids, filterForm.zone_ids]);

  const has_data = useMemo(() => {
    if (!systemWiseGroupedAlarmsData || Object.keys(systemWiseGroupedAlarmsData).length < 1) return false;
    return true;
  }, [systemWiseGroupedAlarmsData]);

  const software_dd = useMemo(() => {
    const system_list = getParsedLocalStorage("systems_list", []);
    return system_list.map((obj) => ({ id: obj.id, value: obj.alias }));
  }, []);

  console.log("software_dd", software_dd);
  useEffect(() => {
    let socket;

    const strCustomerIds = finalFilters.customer_ids.length
      ? getStrIdListFromOptions(finalFilters.customer_ids)
      : getStrIdListFromOptions(customer_dd);
    const handelWebsocketAlarmMessage = (alarm_packet) => {
      const params = {
        customer_ids: strCustomerIds,
        software_ids: finalFilters.software_ids?.length
          ? finalFilters?.software_ids?.map((obj) => obj.id).join(",")
          : null,
      };
      if (alarm_packet.clear_dt) {
        getSystemAlarms(params, true);
      }
      if (alarm_packet.created_dt && alarm_packet.is_active && !alarm_packet.updated_dt) {
        getSystemAlarms(params, true);
      }
    };
    function websocketConnect() {
      const url = `${APP_BASE_URL_ALARM_SOCKET + ALARM_WEBSOCKET_PATH}/ALARMS?customer_ids=${strCustomerIds}`;
      socket = new WebSocket(url);
      socket.onmessage = (event) => {
        const alarm = JSON.parse(event.data);
        if (!alarm) return;
        handelWebsocketAlarmMessage(alarm);
      };

      socket.onerror = function () {
        socket.close();
      };
    }
    websocketConnect();
    return () => {
      if (socket) {
        socket.close();
        socket = null;
      }
    };
  }, [selectedCustomersList, finalFilters]);

  useEffect(() => {
    const params = {
      customer_ids: finalFilters.customer_ids.length
        ? getStrIdListFromOptions(finalFilters.customer_ids)
        : getStrIdListFromOptions(customer_dd),
      // customer_ids: finalFilters.customer_ids.length
      //   ? getStrIdListFromOptions(finalFilters.customer_ids)
      //   : permittedCustomerIds.join(","),
      software_ids: finalFilters.software_ids?.length
        ? finalFilters?.software_ids?.map((obj) => obj.id).join(",")
        : null,
    };

    getSystemAlarms(params);
  }, [permittedCustomerIds, finalFilters]);
  const getSystemAlarms = async (params, silent_update = false) => {
    const url = `${APP_BASE_URL_ALARM}${SYSTEM_ALARMS}`;
    if (!silent_update) {
      setSystemAlarmsData((prev) => {
        prev.isLoading = true;
      });
    }

    let parsed_alarms = [];
    try {
      const _res = await axios(url, { params });
      const res = _res?.data;
      if (!res.length) {
        throw new Error("No Systems Found");
      }
      parsed_alarms = res;
    } catch (err) {
      parsed_alarms = [];

      toast.error(err.message);
    }
    setSystemAlarmsData((prev) => {
      prev.isLoading = false;
      prev.data = parsed_alarms;
    });
  };
  const handleFilterChange = (filter_key, filter_value) => {
    setFilterForm((prev) => {
      prev[filter_key] = filter_value;
    });
  };
  console.log("systemAlarmsData", systemAlarmsData);
  return (
    <div className="system-dashboard ">
      <div className="system-dashboard-filters app--custome--filter-container">
        <Autocomplete
          isOptionEqualToValue={(option, value) => option.id === value.id}
          multiple
          className="filter-form-item"
          options={zone_dd ?? []}
          value={filterForm.zone_ids || []}
          onChange={(_, newValue) => {
            handleFilterChange("zone_ids", newValue);
          }}
          disableCloseOnSelect
          getOptionLabel={(opt) => opt.value}
          renderInput={(params) => <TextField {...params} label="Zones" />}
          renderTags={(value, getTagProps) => {
            return (
              <div>
                <Chip size="small" variant="outlined" label={value[0].value} {...getTagProps(0)} />
                {value.length > 1 && (
                  <AutocompleteStationListPop title={value.map((option) => option?.value ?? "").toString()}>
                    <Chip size="small" label={`+${value.length - 1}`} />
                  </AutocompleteStationListPop>
                )}
              </div>
            );
          }}
        />
        <Autocomplete
          isOptionEqualToValue={(option, value) => option.id === value.id}
          multiple
          disableCloseOnSelect
          className="filter-form-item"
          options={div_dd ?? []}
          value={filterForm.div_ids || []}
          onChange={(_, newValue) => {
            handleFilterChange("div_ids", newValue);
          }}
          getOptionLabel={(opt) => opt.value}
          renderInput={(params) => <TextField {...params} label="Divisions" />}
          renderTags={(value, getTagProps) => {
            return (
              <div>
                <Chip size="small" variant="outlined" label={value[0].value} {...getTagProps(0)} />
                {value.length > 1 && (
                  <AutocompleteStationListPop title={value.map((option) => option?.value ?? "").toString()}>
                    <Chip size="small" label={`+${value.length - 1}`} />
                  </AutocompleteStationListPop>
                )}
              </div>
            );
          }}
        />
        <Autocomplete
          isOptionEqualToValue={(option, value) => option.id === value.id}
          multiple
          disableCloseOnSelect
          className="filter-form-item"
          options={customer_dd ?? []}
          value={filterForm.customer_ids || []}
          onChange={(_, newValue) => {
            handleFilterChange("customer_ids", newValue);
          }}
          getOptionLabel={(opt) => opt.value}
          renderInput={(params) => <TextField {...params} label="Stations" />}
          renderTags={(value, getTagProps) => {
            return (
              <div>
                <Chip size="small" variant="outlined" label={value[0].value} {...getTagProps(0)} />
                {value.length > 1 && (
                  <AutocompleteStationListPop title={value.map((option) => option?.value ?? "").toString()}>
                    <Chip size="small" label={`+${value.length - 1}`} />
                  </AutocompleteStationListPop>
                )}
              </div>
            );
          }}
        />
        <Autocomplete
          isOptionEqualToValue={(option, value) => option.id === value.id}
          multiple
          disableCloseOnSelect
          className="filter-form-item"
          options={software_dd ?? []}
          value={filterForm?.software_ids || []}
          onChange={(_, newValue) => {
            handleFilterChange("software_ids", newValue);
          }}
          getOptionLabel={(opt) => opt.value}
          renderInput={(params) => <TextField {...params} label="Systems" />}
          renderTags={(value, getTagProps) => {
            return (
              <div>
                <Chip size="small" variant="outlined" label={value[0].value} {...getTagProps(0)} />
                {value.length > 1 && (
                  <AutocompleteStationListPop title={value.map((option) => option?.value ?? "").toString()}>
                    <Chip size="small" label={`+${value.length - 1}`} />
                  </AutocompleteStationListPop>
                )}
              </div>
            );
          }}
        />
        <Button
          variant="contained"
          onClick={() => {
            setFinalFilters(filterForm);
          }}
        >
          Search
        </Button>
        <Button
          variant="outlined"
          onClick={() => {
            const initFilters = getInitFilterFrom();
            setFilterForm(initFilters);
            setFinalFilters(initFilters);
          }}
        >
          Reset
        </Button>
      </div>

      <div className="system-cards-container app--custome--card no--transition">
        {systemAlarmsData.isLoading && <Loader />}
        <div className={`cards ${has_data ? "" : "cards__empltyData"}`}>
          {!systemAlarmsData.isLoading &&
            has_data &&
            sortByLastAlarmRecentToOld(Object.values(systemWiseGroupedAlarmsData))
            .filter((obj) => Boolean(obj.prediction_cnt + obj.failure_cnt))
            .map((obj) => {
              return <StatusCard key={obj.software_code} data={obj} />;
            })}
          {!systemAlarmsData.isLoading && !has_data && <MoDataComp />}
        </div>
      </div>
    </div>
  );
}

export default SystemDashboard;
